import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/* ─── Nav structure ─────────────────────────────────────────── */
interface SubItem {
  label: string;
  href?: string;
  desc: string;
  icon: string;
  soon?: boolean;
  children?: SubItem[];
}

interface ServiceCategory {
  category: string;
  icon: string;
  href?: string;
  items: SubItem[];
}

const SERVICE_CATEGORIES: ServiceCategory[] = [
  {
    category: 'Installation & Upgrades',
    icon: 'ri-flashlight-fill',
    href: '/services/installation-upgrades',
    items: [
      { label: 'Electrical Installations', href: '/services/installation-upgrades/electrical-installations', desc: 'New circuits, rewires & full fit-outs', icon: 'ri-plug-fill' },
      { label: 'Full & Partial Rewiring', href: '/services/installation-upgrades/rewiring', desc: 'Complete domestic rewiring, fully certified', icon: 'ri-home-fill' },
      { label: 'Consumer Unit Upgrades', href: '/services/installation-upgrades/consumer-unit-upgrades', desc: 'Modern RCBO fuse board upgrades', icon: 'ri-dashboard-fill' },
      { label: 'Lighting Installation', href: '/services/installation-upgrades/lighting-installation', desc: 'Spotlights, pendants, strips & more', icon: 'ri-lightbulb-flash-fill' },
      { label: 'LED Lighting Upgrades', href: '/services/installation-upgrades/led-lighting-upgrades', desc: 'Cut bills by up to 80% with LED', icon: 'ri-sun-fill' },
      { label: 'Outdoor & Garden Lighting', href: '/services/installation-upgrades/outdoor-garden-lighting', desc: 'Pathway, landscape & feature lighting', icon: 'ri-seedling-fill' },
      { label: 'Security Lighting', href: '/services/installation-upgrades/security-lighting', desc: 'PIR floodlights & motion sensors', icon: 'ri-alarm-warning-fill' },
      { label: 'Socket & Switch Installation', href: '/services/installation-upgrades/socket-switch-installation', desc: 'Extra sockets, USB outlets & smart switches', icon: 'ri-plug-2-fill' },
      { label: 'EV Charger Installation', href: '/services/installation-upgrades/ev-charger-installation', desc: 'OZEV authorised home EV charging', icon: 'ri-car-fill' },
      { label: 'Smart Home Installations', href: '/services/installation-upgrades/smart-home-installations', desc: 'App-controlled lighting & systems', icon: 'ri-home-wifi-fill' },
      { label: 'Home Automation Systems', href: '/services/installation-upgrades/home-automation', desc: 'Full home automation & motorised blinds', icon: 'ri-remote-control-fill' },
      { label: 'Heating Controls', href: '/services/installation-upgrades/heating-controls', desc: 'Smart thermostats & zone control', icon: 'ri-temp-hot-fill' },
      { label: 'Electric Shower Installation', href: '/services/installation-upgrades/electric-shower-installation', desc: 'Zone-compliant shower wiring & bonding', icon: 'ri-water-flash-fill' },
      { label: 'Kitchen Electrical', href: '/services/installation-upgrades/kitchen-electrical', desc: 'Cooker circuits, extractor & LED strips', icon: 'ri-restaurant-fill' },
    ],
  },
  {
    category: 'Repair & Maintenance',
    icon: 'ri-tools-fill',
    href: '/services/repair-maintenance',
    items: [
      { label: 'Electrical Repairs', href: '/services/repair-maintenance/electrical-repairs', desc: 'Fast repairs for all domestic & commercial faults', icon: 'ri-tools-fill' },
      { label: 'Fault Finding & Diagnostics', href: '/services/repair-maintenance/fault-finding', desc: 'Professional equipment, root cause resolved', icon: 'ri-search-eye-fill' },
      { label: 'Tripping Circuits', href: '/services/repair-maintenance/tripping-circuits', desc: 'RCD & MCB trips diagnosed and fixed', icon: 'ri-dashboard-fill' },
      { label: 'Fuse Box Repairs', href: '/services/repair-maintenance/fuse-box-repairs', desc: 'Consumer unit faults repaired safely', icon: 'ri-flashlight-fill' },
      { label: 'Lighting Repairs', href: '/services/repair-maintenance/lighting-repairs', desc: 'Flickering, dead circuits & failed fittings', icon: 'ri-lightbulb-flash-fill' },
      { label: 'Socket & Switch Repairs', href: '/services/repair-maintenance/socket-switch-repairs', desc: 'Sparking, dead or damaged outlets replaced', icon: 'ri-plug-fill' },
      { label: 'Emergency Call-Outs', href: '/services/repair-maintenance/emergency-call-outs', desc: 'Urgent response for dangerous faults', icon: 'ri-alarm-warning-fill' },
      { label: 'Maintenance Contracts', href: '/services/repair-maintenance/maintenance-contracts', desc: 'Scheduled maintenance for landlords & businesses', icon: 'ri-calendar-fill' },
      { label: 'Appliance Repairs', href: '/services/repair-maintenance/appliance-repairs', desc: 'Cooker, shower & appliance circuit faults', icon: 'ri-restaurant-fill' },
    ],
  },
  {
    category: 'Safety & Compliance',
    icon: 'ri-file-certificate-fill',
    href: '/services/safety-compliance',
    items: [
      { label: 'EICR Reports', href: '/services/safety-compliance/eicr-reports', desc: 'Landlord & homeowner certificates', icon: 'ri-file-certificate-fill' },
      { label: 'PAT Testing', href: '/services/safety-compliance/pat-testing', desc: 'Portable appliance testing', icon: 'ri-bar-chart-fill' },
      { label: 'Fire Alarm Testing', href: '/services/safety-compliance/fire-alarm-testing', desc: 'BS 5839 inspection & certification', icon: 'ri-alarm-warning-fill' },
      { label: 'Emergency Lighting', href: '/services/safety-compliance/emergency-lighting', desc: 'Escape lighting inspection & certs', icon: 'ri-flashlight-fill' },
      { label: 'Landlord Electrical Safety', href: '/services/safety-compliance/landlord-electrical-safety', desc: 'Full rental compliance service', icon: 'ri-home-fill' },
      { label: 'Pre-Purchase Survey', href: '/services/safety-compliance/pre-purchase-survey', desc: 'Know your property before you buy', icon: 'ri-search-eye-fill' },
      { label: 'Smoke & CO Alarms', href: '/services/safety-compliance/smoke-co-alarms', desc: 'Alarm installation & compliance', icon: 'ri-alarm-warning-fill' },
      { label: 'HMO Compliance', href: '/services/safety-compliance/hmo-compliance', desc: 'Full HMO licence documentation', icon: 'ri-building-2-fill' },
      { label: 'Solar PV Inspection', href: '/services/safety-compliance/solar-pv-inspection', desc: 'Solar system safety & performance', icon: 'ri-sun-fill' },
      { label: 'Electrical Safety Surveys', href: '/services/safety-compliance/electrical-safety-surveys', desc: 'Written condition surveys', icon: 'ri-search-eye-fill' },
      { label: 'Commercial Compliance', href: '/services/safety-compliance/commercial-compliance', desc: 'EICR, PAT & fire for businesses', icon: 'ri-building-fill' },
      { label: 'Remedial Works', href: '/services/safety-compliance/remedial-works', desc: 'C1 & C2 fixes, Satisfactory EICR', icon: 'ri-tools-fill' },
    ],
  },
  {
    category: 'Commercial',
    icon: 'ri-building-fill',
    href: '/services/commercial',
    items: [
      { label: 'Commercial Installations', href: '/services/commercial/commercial-installations', desc: 'Offices, retail & commercial fit-outs', icon: 'ri-building-fill' },
      { label: 'Three Phase Power', href: '/services/commercial/three-phase-power', desc: 'Heavy load & three phase wiring', icon: 'ri-equalizer-fill' },
      { label: 'Commercial Rewiring', href: '/services/commercial/commercial-rewiring', desc: 'Business rewires, minimal disruption', icon: 'ri-flashlight-fill' },
      { label: 'Office Fit-Outs', href: '/services/commercial/office-fit-out', desc: 'First fix to final sign-off', icon: 'ri-briefcase-fill' },
      { label: 'Data & IT Cabling', href: '/services/commercial/data-cabling', desc: 'Cat6, server rooms & IT power', icon: 'ri-wifi-fill' },
      { label: 'Commercial Lighting', href: '/services/commercial/commercial-lighting', desc: 'LED upgrades & feature lighting', icon: 'ri-lightbulb-flash-fill' },
      { label: 'Commercial EV Charging', href: '/services/commercial/commercial-ev-charging', desc: 'Workplace & car park charge points', icon: 'ri-car-fill' },
      { label: 'Retail Electrical', href: '/services/commercial/retail-electrical', desc: 'Display lighting, tills & signage', icon: 'ri-store-fill' },
      { label: 'Warehouse & Industrial', href: '/services/commercial/warehouse-industrial', desc: 'High-bay lighting & industrial power', icon: 'ri-community-fill' },
      { label: 'Commercial Maintenance', href: '/services/commercial/commercial-maintenance', desc: 'Planned maintenance contracts', icon: 'ri-calendar-fill' },
      { label: 'Commercial EICR', href: '/services/commercial/commercial-eicr', desc: 'Business electrical inspections', icon: 'ri-file-certificate-fill' },
    ],
  },
];

/* ─── Scroll-or-navigate helper ─────────────────────────────── */
function useNavAction() {
  const navigate = useNavigate();
  const location = useLocation();

  const goSection = useCallback((id: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 450);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [navigate, location.pathname]);

  const goPage = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  return { goSection, goPage, pathname: location.pathname };
}

/* ─── Main Navbar ────────────────────────────────────────────── */
export default function Navbar() {
  const { goSection, goPage, pathname } = useNavAction();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const servicesButtonRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* scroll detection */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* active section on homepage */
  useEffect(() => {
    if (pathname !== '/') { setActiveSection(''); return; }
    const ids = ['home', 'about', 'services', 'reviews', 'gallery', 'contact'];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [pathname]);

  /* close dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        servicesButtonRef.current && !servicesButtonRef.current.contains(e.target as Node)
      ) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* close mobile menu on route change */
  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  const handleServicesMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    closeTimer.current = setTimeout(() => setServicesOpen(false), 150);
  };

  const isServiceActive = pathname.startsWith('/services');

  const navLinkClass = (active: boolean) =>
    `relative text-xs tracking-widest uppercase font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap group ${
      active ? 'text-[#D4AF37]' : 'text-white/75 hover:text-white'
    }`;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled || mobileOpen
            ? 'bg-[#07101F]/97 backdrop-blur-md border-b border-[#C8A96A]/15'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-[72px] md:h-[80px]">

          {/* Logo */}
          <button
            onClick={() => goSection('home')}
            className="flex items-center gap-3 cursor-pointer flex-shrink-0"
          >
            <img
              src="https://storage.readdy-site.link/project_files/00423dcc-69f9-4748-ac0c-c15d54e5586b/90758309-4e57-4704-ada6-1299ec143e1f_3eb45589-c339-4fd1-9e2d-32cca06dacf9-removebg-preview.png?v=2a77e748ecd680d6c594c6350bb285eb"
              alt="Quantum Electrical Services Logo"
              className="h-12 w-12 md:h-14 md:w-14 object-contain flex-shrink-0"
              style={{ filter: 'drop-shadow(0 0 8px rgba(200,169,106,0.55))' }}
            />
            <div className="block">
              <p className="text-[#C8A96A] font-bold text-xs md:text-sm tracking-widest uppercase leading-none"
                style={{ fontFamily: "'Montserrat', sans-serif" }}>Quantum Electrical</p>
              <p className="text-white/55 text-[10px] tracking-wider uppercase leading-none mt-1">Services · Sleaford</p>
            </div>
          </button>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">

            {/* Home */}
            <button
              onClick={() => goSection('home')}
              className={`${navLinkClass(pathname === '/' && activeSection === 'home')} px-4 py-2`}
            >
              Home
              <span className={`absolute -bottom-px left-4 right-4 h-px bg-gradient-to-r from-[#C8A96A] to-[#D4AF37] transition-all duration-300 ${pathname === '/' && activeSection === 'home' ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`} />
            </button>

            {/* Services with dropdown */}
            <div
              className="relative"
              onMouseEnter={handleServicesMouseEnter}
              onMouseLeave={handleServicesMouseLeave}
            >
              <button
                ref={servicesButtonRef}
                onClick={() => setServicesOpen((v) => !v)}
                className={`${navLinkClass(isServiceActive)} px-4 py-2 flex items-center gap-1.5`}
              >
                Services
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                <span className={`absolute -bottom-px left-4 right-4 h-px bg-gradient-to-r from-[#C8A96A] to-[#D4AF37] transition-all duration-300 ${isServiceActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`} />
              </button>

              {/* Mega dropdown */}
              <div
                ref={dropdownRef}
                onMouseEnter={handleServicesMouseEnter}
                onMouseLeave={handleServicesMouseLeave}
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[800px] transition-all duration-300 origin-top ${
                  servicesOpen
                    ? 'opacity-100 scale-y-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 scale-y-95 -translate-y-2 pointer-events-none'
                }`}
                style={{ transformOrigin: 'top center' }}
              >
                {/* Arrow */}
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-[#0D1E35] border-l border-t border-[#C8A96A]/20" />

                <div
                  className="rounded-2xl border border-[#C8A96A]/20 overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #0D1E35 0%, #091729 100%)', boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.08)' }}
                >
                  {/* Top bar */}
                  <div className="px-6 py-4 border-b border-[#C8A96A]/10 flex items-center justify-between"
                    style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.08) 0%, transparent 100%)' }}>
                    <div>
                      <p className="text-white font-bold text-sm tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        Our Services
                      </p>
                      <p className="text-white/40 text-xs mt-0.5">NAPIT registered electrical services across Sleaford &amp; Lincolnshire</p>
                    </div>
                    <span className="text-[10px] font-semibold tracking-widest uppercase text-[#D4AF37]/70 border border-[#D4AF37]/30 px-3 py-1 rounded-full">
                      Sleaford, Lincolnshire
                    </span>
                  </div>

                  {/* Service columns */}
                  <div className="grid grid-cols-4 gap-0 divide-x divide-[#C8A96A]/10">
                    {SERVICE_CATEGORIES.map((cat) => {
                      const MAX_VISIBLE = 8;
                      const visibleItems = cat.items.slice(0, MAX_VISIBLE);
                      const hiddenCount = cat.items.length - MAX_VISIBLE;
                      return (
                        <div key={cat.category} className="p-4 flex flex-col">
                          {/* Category heading */}
                          <button
                            onClick={() => { if (cat.href) { goPage(cat.href); setServicesOpen(false); } }}
                            className={`w-full text-left mb-3 group/cat ${cat.href ? 'cursor-pointer' : 'cursor-default'}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-6 h-6 flex items-center justify-center rounded-md bg-[#D4AF37]/15 flex-shrink-0">
                                <i className={`${cat.icon} text-[#D4AF37] text-xs`} />
                              </div>
                              <span className={`text-[11px] font-bold tracking-wider uppercase leading-tight ${cat.href ? 'text-[#D4AF37] group-hover/cat:text-[#F0D078]' : 'text-[#D4AF37]/60'} transition-colors duration-200`}>
                                {cat.category}
                              </span>
                            </div>
                          </button>

                          {/* Sub-items */}
                          <div className="flex flex-col gap-0.5 flex-1">
                            {visibleItems.map((item) => (
                              <button
                                key={item.label}
                                onClick={() => {
                                  if (item.href && !item.soon) { goPage(item.href); setServicesOpen(false); }
                                }}
                                disabled={item.soon}
                                className={`w-full text-left p-1.5 rounded-lg transition-all duration-200 group/item ${
                                  item.soon
                                    ? 'opacity-40 cursor-not-allowed'
                                    : 'hover:bg-[#D4AF37]/08 cursor-pointer'
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <i className={`${item.icon} text-[10px] ${item.soon ? 'text-white/30' : 'text-[#C8A96A]/60 group-hover/item:text-[#D4AF37]'} transition-colors duration-200`} />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className={`text-[11px] font-medium leading-tight ${item.soon ? 'text-white/30' : 'text-white/80 group-hover/item:text-white'} transition-colors duration-200`}>
                                        {item.label}
                                      </span>
                                      {item.soon && (
                                        <span className="text-[9px] font-bold tracking-widest uppercase text-[#D4AF37]/40 border border-[#D4AF37]/20 px-1 py-px rounded-full whitespace-nowrap">
                                          Soon
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>

                          {/* View all overflow link */}
                          {(hiddenCount > 0 || cat.href) && (
                            <button
                              onClick={() => { if (cat.href) { goPage(cat.href); setServicesOpen(false); } }}
                              className="mt-2 pt-2 border-t border-[#C8A96A]/10 flex items-center gap-1.5 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors duration-200 cursor-pointer group/all"
                            >
                              <i className="ri-apps-2-fill text-[10px]" />
                              <span className="text-[10px] font-semibold tracking-wide">
                                {hiddenCount > 0 ? `+${hiddenCount} more services` : 'View all services'}
                              </span>
                              <i className="ri-arrow-right-s-line text-xs group-hover/all:translate-x-0.5 transition-transform duration-200" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer bar */}
                  <div className="px-6 py-3 border-t border-[#C8A96A]/10 flex items-center justify-between"
                    style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <p className="text-white/30 text-[10px]">All work NAPIT certified · Part P compliant · Fully insured</p>
                    <button
                      onClick={() => { goPage('/contact'); setServicesOpen(false); }}
                      className="text-[10px] font-semibold tracking-wider uppercase text-[#D4AF37] hover:text-[#F0D078] transition-colors duration-200 cursor-pointer flex items-center gap-1 whitespace-nowrap"
                    >
                      <i className="ri-phone-fill text-xs" />
                      Free Quote
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Gallery */}
            <button
              onClick={() => goPage('/gallery')}
              className={`${navLinkClass(pathname === '/gallery')} px-4 py-2`}
            >
              Gallery
              <span className={`absolute -bottom-px left-4 right-4 h-px bg-gradient-to-r from-[#C8A96A] to-[#D4AF37] transition-all duration-300 ${pathname === '/gallery' ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`} />
            </button>

            {/* Contact */}
            <button
              onClick={() => goPage('/contact')}
              className={`${navLinkClass(pathname === '/contact')} px-4 py-2`}
            >
              Contact
              <span className={`absolute -bottom-px left-4 right-4 h-px bg-gradient-to-r from-[#C8A96A] to-[#D4AF37] transition-all duration-300 ${pathname === '/contact' ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`} />
            </button>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:01529265060"
              className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37] text-sm transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full border border-[#C8A96A]/30 hover:border-[#C8A96A]/60 transition-colors duration-300">
                <i className="ri-phone-fill text-sm text-[#C8A96A]" />
              </div>
              <span className="font-semibold tracking-wide text-sm">01529 265060</span>
            </a>
            <button
              onClick={() => goPage('/contact')}
              className="flex items-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#D4AF37] hover:to-[#C8A96A] text-[#07101F] font-bold text-xs tracking-widest uppercase px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Free Quote
            </button>
          </div>

          {/* Tablet CTA + Hamburger */}
          <div className="lg:hidden flex items-center gap-3">
            <a
              href="tel:01529265060"
              className="hidden sm:flex items-center gap-1.5 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] text-[#07101F] font-bold text-xs px-4 py-2 rounded-full whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xs" />
              01529 265060
            </a>
            <button
              className="w-10 h-10 flex items-center justify-center text-[#C8A96A] cursor-pointer rounded-full border border-[#C8A96A]/20 hover:border-[#C8A96A]/40 transition-colors duration-200"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl transition-all duration-300 ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-400 ease-in-out ${
            mobileOpen ? 'max-h-[90vh] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div
            className="border-t border-[#C8A96A]/15 px-4 py-5 flex flex-col gap-1 overflow-y-auto"
            style={{ background: 'linear-gradient(180deg, #07101F 0%, #0A1628 100%)', maxHeight: 'calc(90vh - 80px)' }}
          >
            {/* Home */}
            <button
              onClick={() => { goSection('home'); setMobileOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/80 hover:text-white hover:bg-white/5 transition-all duration-200 cursor-pointer text-sm font-medium tracking-wide uppercase text-left"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D4AF37]/10 flex-shrink-0">
                <i className="ri-home-4-fill text-[#D4AF37] text-sm" />
              </div>
              Home
            </button>

            {/* Services accordion */}
            <div>
              <button
                onClick={() => setMobileServicesOpen((v) => !v)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-200 cursor-pointer text-sm font-medium tracking-wide uppercase text-left ${
                  mobileServicesOpen ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-white/80 hover:text-white hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D4AF37]/10 flex-shrink-0">
                    <i className="ri-flashlight-fill text-[#D4AF37] text-sm" />
                  </div>
                  Services
                </div>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Accordion body */}
              <div
                className={`overflow-hidden transition-all duration-400 ease-in-out ${
                  mobileServicesOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="ml-4 mt-1 border-l border-[#C8A96A]/15 pl-4 flex flex-col gap-1 pb-2">
                  {SERVICE_CATEGORIES.map((cat) => (
                    <div key={cat.category}>
                      {/* Category */}
                      <button
                        onClick={() => {
                          setExpandedCategory(expandedCategory === cat.category ? null : cat.category);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors duration-200"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                            <i className={`${cat.icon} text-[#D4AF37]/70 text-xs`} />
                          </div>
                          <span className="text-[11px] font-bold tracking-widest uppercase text-[#D4AF37]/80 text-left">
                            {cat.category}
                          </span>
                          {cat.href && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); goPage(cat.href!); setMobileOpen(false); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); goPage(cat.href!); setMobileOpen(false); } }}
                              className="text-[10px] text-[#D4AF37]/40 hover:text-[#D4AF37] cursor-pointer transition-colors duration-200"
                            >
                              →
                            </span>
                          )}
                        </div>
                        <i className={`ri-arrow-down-s-line text-[#D4AF37]/40 text-sm transition-transform duration-300 ${expandedCategory === cat.category ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Sub-items */}
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          expandedCategory === cat.category ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="ml-2 flex flex-col gap-0.5 py-1">
                          {cat.items.map((item) => (
                            <button
                              key={item.label}
                              onClick={() => { if (item.href && !item.soon) { goPage(item.href); setMobileOpen(false); } }}
                              disabled={item.soon}
                              className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-200 ${
                                item.soon ? 'opacity-35 cursor-not-allowed' : 'hover:bg-[#D4AF37]/08 cursor-pointer'
                              }`}
                            >
                              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                <i className={`${item.icon} text-[#C8A96A]/50 text-xs`} />
                              </div>
                              <span className="text-white/65 text-xs font-medium">{item.label}</span>
                              {item.soon && (
                                <span className="ml-auto text-[9px] font-bold tracking-widest uppercase text-[#D4AF37]/35 border border-[#D4AF37]/15 px-1.5 py-px rounded-full whitespace-nowrap">
                                  Soon
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gallery */}
            <button
              onClick={() => { goPage('/gallery'); setMobileOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/80 hover:text-white hover:bg-white/5 transition-all duration-200 cursor-pointer text-sm font-medium tracking-wide uppercase text-left"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D4AF37]/10 flex-shrink-0">
                <i className="ri-image-2-fill text-[#D4AF37] text-sm" />
              </div>
              Gallery
            </button>

            {/* Contact */}
            <button
              onClick={() => { goPage('/contact'); setMobileOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/80 hover:text-white hover:bg-white/5 transition-all duration-200 cursor-pointer text-sm font-medium tracking-wide uppercase text-left"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D4AF37]/10 flex-shrink-0">
                <i className="ri-message-3-fill text-[#D4AF37] text-sm" />
              </div>
              Contact
            </button>

            {/* Mobile CTA */}
            <div className="mt-3 pt-4 border-t border-[#C8A96A]/10 flex flex-col sm:flex-row gap-3">
              <a
                href="tel:01529265060"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] text-[#07101F] font-bold text-sm px-5 py-3 rounded-full whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill" />
                01529 265060
              </a>
              <button
                onClick={() => { goSection('contact'); setMobileOpen(false); }}
                className="flex items-center justify-center gap-2 border border-[#C8A96A]/35 text-[#C8A96A] hover:bg-[#C8A96A]/10 font-medium text-sm px-5 py-3 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line" />
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </>
  );
}
